import { boolean, object, string } from 'yup';

import '@/extensions/yup';
import type { SiteId } from '@/features/basket/types/site';
import { useTranslations } from 'next-intl';
import type { PostalCodeLocale } from 'validator';

export const postalCodeLocale: Record<SiteId, PostalCodeLocale> = {
  wp_pl: 'PL',
  ld_lt: 'LT',
  ds_lv: 'LV',
  kg_ee: 'EE',
  el_fi: 'FI',
};

export const getPostalCodeLocale = (locale: SiteId) => postalCodeLocale[locale];

export const getPostalCodeTransform =
  (locale: PostalCodeLocale) => (value: string) => {
    switch (locale) {
      case 'PL':
        return value.charAt(2) === '-'
          ? value
          : `${value.slice(0, 2)}-${value.slice(2)}`;
      case 'LT':
        return value.startsWith('LT-') ? value : `LT-${value}`;
      case 'LV':
        return value.startsWith('LV-') ? value : `LV-${value}`;
      default:
        return value;
    }
  };

export const usePasswordSchema = () => {
  const t = useTranslations('common.password.rules');

  return string()
    .required(() => null) // This is so usePasswordSchema is of type `string` and not `string | undefined`
    .min(8, ({ min }) => t('length', { min }))
    .matches(/[a-z]/, t('small_letter'))
    .matches(/[A-Z]/, t('big_letter'))
    .matches(/\d/, t('digit'))
    .matches(/[^\w\s]/, t('special_character'))
    .max(64, ({ max }) => t('max_length', { max }));
};

export const usePasswordObjectSchema = () =>
  object({ newPassword: usePasswordSchema(), consent: boolean().required() });

export const yupEmailSchema = () => string().email();

export const useEmailObjectSchema = () =>
  object().shape({
    email: yupEmailSchema().required(),
  });

export const loginAsGuestObjectSchema = () =>
  object().shape({
    email: yupEmailSchema().required(),
    consent: boolean().required(),
  });

export const useStepLoginSchema = () =>
  object().shape({
    email: yupEmailSchema().required(),
    password: string(),
  });

export const useChangePasswordSchema = () =>
  object({
    currentPassword: string().required(),
    newPassword: usePasswordSchema(),
  });

export const useDeleteAccountSchema = () =>
  object({
    password: string().required(),
    checkbox: boolean().oneOf([true], 'required').required('required'),
  });

export const useNewsletterSchema = () =>
  object({
    email: yupEmailSchema().required(),
    accept: boolean().required(),
  });
